<template>
  <div class="container">
    <b-form @submit.prevent="onSendForm(newProject)" novalidate>
      <div class="row justify-content-between m-0 mt-1 align-items-stretch">
        <div class="col-sm-12 col-md-10 col-lg-10 d-flex">
          <ProjectBanner
            class="flex-grow-1"
            @banner-updated="onBannerUpdated"
            @banner-removed="onBannerRemoved"
          />
        </div>
        <div class="col-sm-6 col-md-2 col-lg-2 d-flex">
          <ProjectLogo
            class="flex-grow-1"
            @logo-updated="onLogoUpdated"
            @logo-removed="onLogoRemoved">
          </ProjectLogo>
        </div>
      </div>

      <div class="card-header mt-2">
        <div class="row justify-content-between">
          <div class="col-lg-6">
            <h3 class="mb-0">Create a CitSci Project</h3>
          </div>
          <div class="d-flex flex-wrap col-lg-3">
            <button type="submit" class="btn btn-primary mr-2"
                    title="Save Your New CitSci Project as Draft"
                    @click="isDraft = true">
              Save As Draft
            </button>
            <button type="submit"
                    class="btn btn-secondary ml-2"
                    title="Publish Your New CitSci Project Now"
                    @click="isDraft = false">
              Publish Project
            </button>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="row justify-contents-center">
          <div class="col-lg-8 col-md-12">
            <span class="text-danger font-weight-bold">* Required fields</span>
            <div class="form-group mt-3">
              <b-form-group id="project-name-input-group" label-for="project-name-input"
                            label-class="font-weight-bold" label-cols="12">
                <template #label>
                  Project Name <span class="text-danger">*</span>
                </template>
                <b-form-input id="project-name-input" type="text" maxlength="100" name="name"
                              placeholder="Project Name" v-model.trim="newProject.name"
                              v-validate="'required|min:5|max:100'" :class="{
                      'is-invalid': errors.has('name') || errors.has('urlField')
                    }" :state="updateURLField" aria-describedby="project-name-input-live-feedback">
                </b-form-input>
                <b-form-invalid-feedback id="project-name-input-live-feedback">
                  {{ errors.first("name") || errors.first("urlField") }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group id="project-url-input-group" class="mt-3"
                            label-for="project-url-input" label-class="font-weight-bold" label-cols="12">
                <template #label>
                  Project URL
                  <i class="fa fa-question-circle" id="projectUrl-info"></i>
                </template>
                <label class="text-danger text-small">
                  This URL is tied to the Project Name and any future modification to the project name will break
                  any <b>external</b> links to this project
                </label>
                <b-form-textarea id="project-url-textarea" name="urlField" placeholder="Project URL"
                                 v-model="newProject.urlField" rows="1" max-rows="2" no-auto-shrink no-resize
                                 :disabled="true">
                </b-form-textarea>
                <b-btn size="sm" @click="copyURLToClipboard">
                  <i class="fa fa-clipboard fa-lg"></i>
                  &nbsp;Copy
                </b-btn>
              </b-form-group>

              <b-form-group id="project-description-input-group" class="mt-3"
                            label-for="project-description-input" label-class="font-weight-bold h4" label-cols="12">
                <template #label>
                  About <span class="text-danger">*</span>
                </template>
                <b-form-textarea id="project-description-textarea" name="description"
                                 placeholder="Please provide a description of your project (Minimum of 10 characters)"
                                 v-model.trim="newProject.description" v-validate="'required|min:10|max:32000'" data-vv-delay="500"
                                 :class="{ 'is-invalid': errors.has('description') }" :rows="3" :max-rows="8"
                                 aria-describedby="project-description-input-live-feedback">
                </b-form-textarea>
                <b-form-invalid-feedback id="project-description-input-live-feedback">
                  {{ errors.first("description") }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group id="project-goals-input-group" class="mt-3"
                            label-for="project-goals-input"
                            label-class="font-weight-bold h4" label-cols="12">
                <template #label>
                  Goals <span class="text-danger">*</span>
                </template>
                <b-form-textarea id="project-goals-textarea" name="goals"
                                 placeholder="Please describe what your project hopes to accomplish (e.g. Monitor water quality in the St. Lucie River)"
                                 v-model="newProject.goals" v-validate="'required|min:10|max:32000'" data-vv-delay="500"
                                 :class="{ 'is-invalid': errors.has('goals') }" :rows="3" :max-rows="8"
                                 aria-describedby="project-goals-input-live-feedback">
                </b-form-textarea>
                <b-form-invalid-feedback id="project-goals-input-live-feedback">
                  {{ errors.first("goals") }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group id="project-tasks-input-group" class="mt-3"
                            label-for="project-tasks-input"
                            label-class="font-weight-bold h4" label-cols-sm="12">
                <template #label>
                  Tasks <span class="text-danger">*</span>
                </template>
                <b-form-textarea id="project-tasks-textarea" name="tasks"
                                 placeholder="Please describe how the participant will help this project (e.g. Collect and submit water samples for analysis)"
                                 v-model.trim="newProject.tasks" v-validate="'required|min:10|max:64000'" data-vv-delay="500"
                                 :class="{ 'is-invalid': errors.has('tasks') }" :rows="3" :max-rows="8"
                                 aria-describedby="project-tasks-input-live-feedback">
                </b-form-textarea>
                <b-form-invalid-feedback id="project-tasks-input-live-feedback">
                  {{ errors.first("tasks") }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group id="project-topics-input-group" class="mt-3"
                            label-for="project-topics-input"
                            label-class="font-weight-bold h4" label-cols-sm="12">
                <template #label>
                  Topics <span class="text-danger">*</span>
                </template>
                <multiselect id="project-topics-input"
                             v-model="newProject.topics"
                             :options="allTopics"
                             placeholder="Select one or more topics"
                             :multiple="true"
                             :taggable="false"
                             :hideSelected="true"
                             :close-on-select="false"
                             v-validate="'required|min:1'"
                             open-direction="bottom"
                             name="topics"
                             :class="{ 'is-invalid': errors.has('topics') }">
                </multiselect>

                <b-form-invalid-feedback id="project-topics-input-live-feedback">
                  {{ errors.first("topics") }}, Select at least one topic
                </b-form-invalid-feedback>
              </b-form-group>
            </div>
          </div>

          <div class="col-lg-4 col-md-12">
            <div class="card">
              <div class="card-header">
                <h4>
                  Settings
                  <i class="fa fa-xs fa-wrench"></i>
                </h4>
              </div>
              <div class="card-body">
                <div class="d-flex flex-wrap justify-content-between">
                  <div class="mt-2 flex-grow-1 pr-2">
                    <b>Membership:</b> {{ newProject.approveContacts ? 'Member-Based' : 'Open' }}
                  </div>
                  <div class="mt-2 flex-none">
                    <div class="btn btn-primary btn-sm" @click="showMembershipModal()">
                      <i class="mr-2 fa fa-pencil"></i>Edit
                    </div>
                  </div>
                </div>
                <div class="d-flex flex-wrap justify-content-between">
                  <div class="mt-2 flex-grow-1 pr-2">
                    <b>Privacy:</b> {{ newProject.isPrivate ? 'Private' : 'Public' }}
                  </div>
                  <div class="mt-2 flex-none">
                    <div class="btn btn-primary btn-sm" @click="showPrivacyModal()">
                      <i class="mr-2 fa fa-pencil"></i>Edit
                    </div>
                  </div>
                </div>
                <div class="d-flex flex-wrap justify-content-between">
                  <div class="mt-2 flex-grow-1 pr-2">
                    <b>Location Privacy: </b>{{ locationPrivacyText }}
                  </div>
                  <div class="mt-2 flex-none">
                    <b-btn size="sm" variant="primary" @click="showLocationPrivacyModal()">
                      <i class="mr-2 fa fa-pencil"></i>Edit
                    </b-btn>
                  </div>
                </div>
                <div class="d-flex flex-wrap justify-content-between">
                  <div class="mt-2 flex-grow-1 pr-2">
                    <b>Image Metadata Removal: </b><span :class="{'text-danger': newProject.locationPrivacy && newProject.isStripImageMetadata === false}">{{ newProject.isStripImageMetadata ? 'On' : 'Off' }}</span>
                  </div>
                  <div class="mt-2 flex-none">
                    <b-btn size="sm" variant="primary" @click="showImageMetadataModal()">
                      <i class="mr-2 fa fa-pencil"></i>Edit
                    </b-btn>
                  </div>
                </div>
              </div>
            </div>

            <Integrations class="mt-3"
                          :project-state="newProject.projectState ? newProject.projectState : 0"
                          :is-airtable="newProject.isAirtable"
                          :is-sci-starter="newProject.isSciStarter"
                          :is-zooniverse="newProject.isZooniverse">
            </Integrations>

            <div class="card">
              <div class="card-header">
                <h4>Hub Affiliations
                  <span>
                    <i class="fa fa-sitemap"></i>
                    <i class="fa fa-xs fa-question-circle" id="hub-info"></i>
                  </span>
                </h4>
              </div>
              <div class="card-body">
                <ProjectOrgAffiliations v-if="newProject.projectState === PROJECT_STATE_PUBLISHED" new-affiliations="">
                </ProjectOrgAffiliations>
                <label v-else>Hub affiliations are available after you publish this project.</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <h4 class="card-header">
          Location
          <span>
            <i class="fa fa-xs fa-question-circle" id="project-location-info"/>
          </span>
        </h4>
        <vue-google-map :item="newProject" :latitude="newProject && newProject.latitude"
                        :longitude="newProject && newProject.longitude" :is-loading="false" :custom-marker="false"
                        :zoom-map-to-markers="11" :show-locate-address="true" :zoom-val="5" :show-address-flag="true"
                        requiredLatLngFields class="project-create-edit"/>
      </div>

      <div class="card-header">
        <div class="row justify-content-end">
          <div class="d-flex flex-wrap col-lg-3">
            <button type="submit" class="btn btn-primary mr-2"
                    title="Save Your New CitSci Project as Draft"
                    @click="isDraft = true">
              Save As Draft
            </button>
            <button type="submit"
                    class="btn btn-secondary ml-2"
                    title="Publish Your New CitSci Project Now"
                    @click="isDraft = false">
              Publish Project
            </button>
          </div>
        </div>
      </div>

      <b-tooltip target="projectUrl-info" placement="right">
        This will be the web address you can use to have volunteers access your
        CitSci project directly. Simply have your volunteers copy and
        paste this into their web browser to easily access your project
      </b-tooltip>
      <b-tooltip target="hub-info" placement="bottom">
        Associate your project with one or more Organization Hubs and become part of their Hub Page
      </b-tooltip>
      <b-tooltip target="project-location-info" placement="bottom">
        Manually enter coordinates, click on the map, or search for an address to
        obtain coordinates. This represents your project headquarters. Please use
        decimal degrees (not degrees, minutes, seconds).
      </b-tooltip>
    </b-form>
    <ProjectMembershipModal :membershipValue="newProject.approveContacts"
                            @membershipChange="handleMembershipChange"></ProjectMembershipModal>
    <ProjectPrivacyModal :privacyValue="newProject.isPrivate"
                         @privacyChange="handlePrivacyChange"></ProjectPrivacyModal>
      <ProjectLocationPrivacyModal v-model="newProject.locationPrivacy">
      </ProjectLocationPrivacyModal>
      <ProjectImageMetadataModal :image-metadata-removal-value="newProject.isStripImageMetadata"
                                 @imageMetadataChange="handleImageMetadataChange">
      </ProjectImageMetadataModal>
  </div>
</template>

<script>
import {mapGetters, mapActions} from "vuex";
import ProjectOrgAffiliations from "../view/edit/ProjectOrgAffiliations.vue";
import VueGoogleMap from "../../../assets/scripts/layout/GoogleMap";
import ProjectBanner from "./ProjectBanner.vue";
import ProjectLogo from "./ProjectLogo.vue";
import FromNow from "../../layout/FromNow.vue";
import Integrations from "../Integrations.vue";
import ProjectMembershipModal from "../view/edit/ProjectMembershipModal.vue";
import ProjectPrivacyModal from "../view/edit/ProjectPrivacyModal.vue";
import ProjectLocationPrivacyModal from "../view/edit/ProjectLocationPrivacyModal.vue";
import ProjectImageMetadataModal from "../view/edit/ProjectImageMetadataModal.vue";
import {PROJECT_STATE_DRAFT, PROJECT_STATE_PUBLISHED} from "../../../constants/project_states";

export default {
  components: {
    FromNow,
    ProjectBanner,
    ProjectLogo,
    VueGoogleMap,
    ProjectOrgAffiliations,
    Integrations,
    ProjectMembershipModal,
    ProjectPrivacyModal,
    ProjectLocationPrivacyModal,
    ProjectImageMetadataModal,
  },
  provide() {
    return {parentValidator: this.$validator}
  },
  data() {
    return {
      PROJECT_STATE_DRAFT,
      PROJECT_STATE_PUBLISHED,
      newProject: {
        projectState: null,
        approveContacts: true,
        isPrivate: false,
        locationPrivacy: null,
        isStripImageMetadata: true,
        isSciStarter: false,
        isZooniverse: false,
        isAirtable: false,
        picture: null,
        bannerPicture: null,
        organizations: [],
        urlField: null,
      },
      isDraft: false,
      publishButtonTitle: 'Publish Project',
    }
  },

  created() {
    if (this.values) {
      // Initialize topics if not already set
      if (!this.newProject.topics) {
        this.newProject.topics = []
      }
    }

    // Hubs are fetched
    this.getOrganizations();

    // Load the topics
    this.getTopics();
  },

  computed: {
    ...mapGetters({
      isLoading: 'project/create/isLoading',
      error: 'project/create/error',
      created: 'project/create/project',

      organizationsError: 'organization/list/organizationsError',
      isOrganizationsLoading: 'organization/list/isOrganizationsLoading',
      organizationsList: 'organization/list/organizations',

      isTopicsLoading: 'topics/isLoading',
      allTopics: 'topics/allTopics',
      topicsError: 'topics/error',
    }),

    selectedOrganizations: {
      get() {
        if (this.newProject) {
          return [...this.itemVal.organizationProjects.map((op) => op.organization)]
        } else {
          return null
        }
      },
      set(data) {
        // console.log(data)
        this.itemVal.organizationProjects = data
      }
    },

    /**
     * @function updateUrlField
     * @returns {boolean}
     */
    updateURLField() {
      //Strip all special characters except and replace with hyphen
      const special_char_regex = /(\s+|\/|\\|\[|\]|\{|\}|\,|\<|\>|\:|\;|\"|\.|\?|\-|\=|\+|\*|\&|\^|\%|\#|\@|\`|\'|\||\~|\!|\$|\*|\(|\)|\_|\$)+/g;
      const non_ascii_regex = /[\x00-\x08\x0E-\x1F\x7F-\uFFFF]+/g;
      const replace_char = '-'
      this.newProject.urlField = '';

      // If the name is filled, the url is created
      // - basically we replace all special characters and non-ascii values with a hyphen
      if (this.newProject.name && this.newProject.name !== '') {
        // Non-ascii characters (e.g. emoji etc...) are replaced followed by all special character
        let sanitisedUrl = this.newProject.name.toLowerCase()
        sanitisedUrl = sanitisedUrl.replace(special_char_regex, replace_char).replace(non_ascii_regex, replace_char)
        // If the URL ends with a hyphen, it is also removed
        if (sanitisedUrl.endsWith('-')) {
          sanitisedUrl = sanitisedUrl.slice(0, -1)
        }
        // Sanitised URL is set <safe to have only hyphen in the url>
        this.newProject.urlField = this.$appRootUrl + '/projects/' + sanitisedUrl
      }
      return true;
    },
    locationPrivacyText() {
      if (this.newProject && this.newProject.locationPrivacy === undefined || this.newProject.locationPrivacy === null) {
        return "Exact"
      } 
      //return this.newProject.locationPrivacy
      const earthSizeSQKM = 510072000.0; // Earth's surface area in square kilometers
      const privacyLevel = this.newProject.locationPrivacy;
      const privacyAreaSQKM = earthSizeSQKM / (Math.pow(privacyLevel, 2) * 12);
      // round down to nearest 1 decimal place
      const roundedPrivacyAreaSQKM = Math.floor(privacyAreaSQKM * 10) / 10;
      return `${roundedPrivacyAreaSQKM.toLocaleString()} sq km`;
    }
  },

  watch: {
    created(newVal) {
      if (!newVal) {
        return
      }
      this.resetCreate()
      this.$router.push({name: 'ProjectShow', params: {id: newVal['urlField']}})
    },
  },

  methods: {
    ...mapActions({
      create: 'project/create/create',
      resetCreate: 'project/create/reset',
      getOrganizations: 'organization/list/getOrganizations',
      getTopics: 'topics/get',
    }),

    onBannerUpdated(newVal) {
      this.newProject.bannerPicture = newVal
    },

    onBannerRemoved() {
      this.newProject.bannerPicture = null
    },

    onLogoUpdated(newVal) {
      this.newProject.picture = newVal
    },

    onLogoRemoved() {
      this.newProject.picture = null
    },

    showMembershipModal() {
      this.$bvModal.show('project-membership-modal')
    },

    handleMembershipChange(newVal) {
      this.newProject.approveContacts = newVal
    },

    showPrivacyModal() {
      this.$bvModal.show('project-privacy-modal')
    },

    handlePrivacyChange(newVal) {
      this.newProject.isPrivate = newVal
    },

    showLocationPrivacyModal() {
      this.$bvModal.show('project-location-privacy-modal')
    },

    showImageMetadataModal() {
      this.$bvModal.show('project-image-metadata-modal')
    },

    handleImageMetadataChange(newVal) {
      this.newProject.isStripImageMetadata = newVal
    },

    async copyURLToClipboard() {
      try {
        await navigator.clipboard.writeText(this.computedUrlField);
        this.$bvToast.toast("Paste Anywhere", {
          title: "Project URL Copied",
          autoHideDelay: 2500,
          variant: "success"
        });
      } catch ($e) {
        // console.log('copy to clipboard failed.')
      }
    },

    onSendForm(project) {
      // On button press run validation and then create project
      this.$validator.validateAll()
        .then(result => {
          if (result) {
            this.newProject.latitude = parseFloat(this.newProject.latitude);
            this.newProject.longitude = parseFloat(this.newProject.longitude);

            // Project state is set to published | draft
            this.newProject.projectState = this.isDraft ? this.PROJECT_STATE_DRAFT : this.PROJECT_STATE_PUBLISHED;
            const urlParts = this.newProject.urlField.split('/');
            this.newProject.urlField = urlParts[urlParts.length - 1];
            this.create(this.newProject)
          } else {
            this.$el.getElementsByClassName('is-invalid')[0].parentElement.parentElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "center"
            });
          }
        })
    },
  },
}

</script>

<style lang="scss" scoped>

</style>
